// useSortableRows — header-click sortable table helper.
// 3-state cycle: asc → desc → none (natural order).
// Usage:
//   const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(rows, {
//     accessors: { name: (r) => r.name, due: (r) => r.due_date || "" }
//   });
//   <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="name" onClick={toggleSort}>Name</SortableTh>
import { useMemo, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

function defaultAccessor(row, key) {
    // Dot-path support: "client.name"
    return key.split(".").reduce((v, k) => (v == null ? v : v[k]), row);
}

function compareValues(a, b) {
    if (a == null && b == null) return 0;
    if (a == null) return 1;       // nulls last
    if (b == null) return -1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    if (typeof a === "boolean" && typeof b === "boolean") return (a === b) ? 0 : (a ? -1 : 1);
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function useSortableRows(rows, { accessors = {}, defaultKey = null, defaultDir = "asc" } = {}) {
    const [sortKey, setSortKey] = useState(defaultKey);
    const [sortDir, setSortDir] = useState(defaultDir);

    function toggleSort(key) {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDir("asc");
            return;
        }
        if (sortDir === "asc") { setSortDir("desc"); return; }
        // 3rd click: clear sort
        setSortKey(null);
        setSortDir("asc");
    }

    const sorted = useMemo(() => {
        if (!sortKey) return rows;
        const acc = accessors[sortKey] || ((r) => defaultAccessor(r, sortKey));
        const copy = [...rows];
        copy.sort((a, b) => {
            const cmp = compareValues(acc(a), acc(b));
            return sortDir === "asc" ? cmp : -cmp;
        });
        return copy;
    }, [rows, sortKey, sortDir, accessors]);

    return { sortKey, sortDir, toggleSort, sorted };
}

export function SortableTh({ sortKey, sortDir, myKey, onClick, children, className = "", testId, align = "left" }) {
    const active = sortKey === myKey;
    const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
    return (
        <th
            data-testid={testId || `th-${myKey}`}
            onClick={() => onClick(myKey)}
            className={`px-4 py-3 text-[11px] uppercase tracking-wider font-semibold cursor-pointer select-none hover:text-[#1F1F1F] transition ${alignCls} ${active ? "text-[#1F1F1F]" : "text-[#6F6A64]"} ${className}`}
            aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
        >
            <span className="inline-flex items-center gap-1">
                {children}
                {active ? (
                    sortDir === "asc"
                        ? <ChevronUp size={12} className="text-[var(--brand-accent,_#C75A1B)]" />
                        : <ChevronDown size={12} className="text-[var(--brand-accent,_#C75A1B)]" />
                ) : (
                    <span className="opacity-0 group-hover:opacity-40">
                        <ChevronUp size={12} />
                    </span>
                )}
            </span>
        </th>
    );
}
